import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { IDeliveryRequest } from "../../deliveryModule/Types/delivery.interfaces";
import {
  fetchAvailableDeliveries,
  requestDelivery,
  acceptDelivery,
  fetchDeliveryById,
  updateDeliveryStatus,
} from "../../services/delivery/deliveryService";
import { RootState } from "../store";
import axios from "axios";

interface DeliveryState {
  deliveries: IDeliveryRequest[];
  currentDelivery?: IDeliveryRequest | null;
  loading: boolean;
  error: string | null;
}

const initialState: DeliveryState = {
  deliveries: [],
  currentDelivery: null,
  loading: false,
  error: null,
};

interface FetchAvailableArgs {
  userId: string;
  role: "sender" | "driver";
}

export const fetchAvailable = createAsyncThunk(
  "delivery/fetchAvailable",
  async (args: { userId: string; role: "driver" | "sender" }) => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/api/delivery`,
      {
        params: {
          userId: args.userId,
          role: args.role,
        },
      },
    );

    return res.data.data;
  },
);

export const requestNewDelivery = createAsyncThunk(
  "delivery/request",
  async (payload: Partial<IDeliveryRequest>) => await requestDelivery(payload),
);

export const acceptDeliveryById = createAsyncThunk(
  "delivery/accept",
  async ({ deliveryId, driverId }: { deliveryId: string; driverId: string }) =>
    await acceptDelivery(deliveryId, driverId),
);

export const fetchOneDelivery = createAsyncThunk(
  "delivery/fetchOne",
  async (id: string) => await fetchDeliveryById(id),
);

export const updateStatus = createAsyncThunk(
  "delivery/updateStatus",
  async ({
    id,
    status,
  }: {
    id: string;
    status:
      | "requested"
      | "accepted"
      | "assigned"
      | "in_transit"
      | "delivered"
      | "cancelled";
  }) => await updateDeliveryStatus(id, status),
);

const deliverySlice = createSlice({
  name: "delivery",
  initialState,
  reducers: {
    clearCurrentDelivery(state) {
      state.currentDelivery = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailable.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAvailable.fulfilled, (state, action) => {
        state.loading = false;
        state.deliveries = action.payload;
      })
      .addCase(fetchAvailable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Fetch failed";
      })
      .addCase(fetchOneDelivery.fulfilled, (state, action) => {
        state.currentDelivery = action.payload;
      })
      .addCase(requestNewDelivery.fulfilled, (state, action) => {
        state.deliveries.push(action.payload);
      })
      .addCase(acceptDeliveryById.fulfilled, (state, action) => {
        state.currentDelivery = action.payload;
        const index = state.deliveries.findIndex(
          (d) => (d as any)._id === (action.payload as any)._id,
        );
        if (index !== -1) {
          state.deliveries[index] = action.payload;
        }
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        state.currentDelivery = action.payload;
      });
  },
});

export const { clearCurrentDelivery } = deliverySlice.actions;
export default deliverySlice.reducer;

// selector (your store mounts this slice under "deliveryRequests")
export const getDeliveryById = (state: RootState, id: string) =>
  state.deliveryRequests.deliveries.find((d: any) => d._id === id);
